<template>
  <!-- eslint-disable max-len -->
  <div v-if="stats.length > 0" style="background-color: rgba(0, 0, 0, 0);">
    <svg :width="width" :height="height" :viewBox="svgViewBox" style="background-color: #343a40;">
      <!-- dropshadow filter, plus a linear gradient filter for a long shadow effect -->
      <defs>
        <filter id="dropshadow" height="280%" width="280%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="5"/>
          <feOffset dx="2" dy="2" result="offsetblur"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.9"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <linearGradient id="gradient" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="black" stop-opacity="0.7"/>
          <stop offset="55%" stop-opacity="0.2"/>
          <stop offset="70%" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <!-- extra white border -->
      <polygon
        :points="`-1,${height + 1} -1,${height / 5} ${2 * padding},0 ${3 * padding},0 ${padding},${height / 5} ${padding},${height + 1}`"
        fill="white"
        stroke="white"
      ></polygon>
      <!-- gridlines -->
      <g v-if="!isNaN(maxValue) && maxValue > 0">
        <line
          :x1="3 * padding"
          :y1="originPoint - (yIncrement * (n - 1))"
          :x2="width - padding"
          :y2="originPoint - (yIncrement * (n - 1))"
          stroke="white"
          stroke-width="0.25"
          v-for="n in (maxValue+1)"
          :key="n"
        ></line>
      </g>
      <!-- colored filled area graphing the actual values -->
      <g>
        <polygon
          :points="longShadowPoints(n - 1)"
          v-for="n in stats.length"
          :key="n"
          fill="url(#gradient)"
        ></polygon>
      </g>
      <g>
        <polygon
          :points="dropShadowPoints(n - 1)"
          fill="black"
          v-for="n in stats.length"
          :key="n"
          filter="url(#dropshadow)"
        ></polygon>
      </g>
      <g>
        <polygon
          :points="frontPoints(n - 1)"
          :fill="frontColor(n - 1)"
          v-for="n in stats.length"
          :key="n"
        ></polygon>
      </g>
      <g>
        <polygon
          :points="topPoints(n - 1)"
          :fill="topColor(n - 1)"
          v-for="n in stats.length"
          :key="n"
        ></polygon>
      </g>
      <g>
        <polygon
          :points="sidePoints(n - 1)"
          :fill="sideColor(n - 1)"
          v-for="n in stats.length"
          :key="n"
        ></polygon>
      </g>
      <!-- labels -->
      <g>
        <text
          class="barchart-label"
          :x="xCoordinate(index - 1) - (shortNames[index].length * 3.6)"
          :y="(height - (0.75 * padding))"
          font-family="Arial"
          font-size="9pt"
          v-for="index in stats.length"
          :key="index"
          v-text="shortNames[index]"
        >
        </text>
      </g>
      <!-- percentage value labels -->
      <g>
        <text
          class="barchart-percentage"
          :x="xCoordinate(index - 1) - (0.75 * padding)"
          :y="originPoint - (stats[index - 1].value * yIncrement) - (0.25 * padding)"
          font-family="Verdana"
          v-for="index in stats.length"
          :key="index"
          v-text="`${maxValue === 0 ? 0 : Math.round((stats[index - 1].value / valueSum) * 100)}%`"
        >
        </text>
      </g>
    </svg>
  </div>
</template>
<script>
export default {
  name: 'radar',
  props: {
    stats: Array, // NOTE: there is an assumption that values are positive and start at 0
    width: { type: Number, default: 560 },
    height: { type: Number, default: 270 },
    padding: { type: Number, default: 20 },
    barWidth: { type: Number, default: 50 }
  },
  data() {
    return {
      transparent: 'rgba(0, 0, 0, 0)'
    };
  },
  methods: {
    xCoordinate(index) {
      const xOffset = 2 * this.padding;
      const xChartWidth = this.width - xOffset - this.padding;
      const xIncrement = Math.floor(xChartWidth / this.stats.length);
      const xExtra = xChartWidth - (xIncrement * (this.stats.length - 1));
      return (xOffset + (xExtra / 2) + (xIncrement * index));
    },
    frontPoints(index) {
      const xCenter = this.xCoordinate(index);
      const yValue = this.stats[index].value;
      // top right
      const x1 = xCenter + (this.barWidth / 2);
      const y1 = this.originPoint - (yValue * this.yIncrement) + (this.barWidth / 4);
      // bottom left
      const x2 = x1 - this.barWidth;
      const y2 = this.originPoint + (this.barWidth / 4);
      return `${x1},${y1} ${x1},${y2} ${x2},${y2} ${x2},${y1}`;
    },
    topPoints(index) {
      const xCenter = this.xCoordinate(index);
      const yValue = this.stats[index].value;
      // bottom right
      const x1 = xCenter + (this.barWidth / 2);
      const y1 = this.originPoint - (yValue * this.yIncrement) + (this.barWidth / 4);
      // bottom left x
      const x2 = x1 - this.barWidth;
      // x offset (4 different x values)
      const xOffset = this.barWidth / 5;
      // other y value
      const y2 = y1 - (this.barWidth / 4);
      return `${x1},${y1} ${x2},${y1} ${x2 + xOffset},${y2} ${x1 + xOffset},${y2}`;
    },
    sidePoints(index) {
      const xCenter = this.xCoordinate(index);
      const yValue = this.stats[index].value;
      // top left
      const x1 = xCenter + (this.barWidth / 2);
      const y1 = this.originPoint - (yValue * this.yIncrement) + (this.barWidth / 4);
      // bottom left y
      const y2 = this.originPoint + (this.barWidth / 4);
      // y offset (4 different y values)
      const yOffset = -(this.barWidth / 4);
      // other x value
      const x2 = x1 + (this.barWidth / 5);
      return `${x1},${y1} ${x2},${y1 + yOffset} ${x2},${y2 + yOffset} ${x1},${y2}`;
    },
    dropShadowPoints(index) {
      const xCenter = this.xCoordinate(index);
      const yValue = this.stats[index].value;
      // top right
      const x1 = xCenter + (this.barWidth / 2);
      const y1 = this.originPoint - (yValue * this.yIncrement) + (this.barWidth / 4);
      // bottom left
      const x2 = x1 - this.barWidth;
      const y2 = this.originPoint + (this.barWidth / 4);
      // y offset (4 different y values)
      const yOffset = -(this.barWidth / 4);
      // other x value
      const x3 = x1 + (this.barWidth / 5);
      return `${x2},${y2} ${x3},${y1 + yOffset} ${x3},${y2 + yOffset} ${x1},${y2}`;
    },
    longShadowPoints(index) {
      const xCenter = this.xCoordinate(index);
      const yValue = this.stats[index].value;
      // top right
      const x1 = xCenter + (this.barWidth / 2);
      const y1 = this.originPoint - (yValue * this.yIncrement) + (this.barWidth / 4);
      // bottom left
      const x2 = x1 - this.barWidth;
      const y2 = this.originPoint + (this.barWidth / 4);
      // y offset (4 different y values)
      const yOffset = -(this.barWidth / 4);
      // other x value
      const x3 = x1 + (this.barWidth / 5);
      const shadowX = 60;
      const shadowY = 100;
      return `${x2},${y2} ${x3},${y1 + yOffset} ${x3 + shadowX},${y1 + yOffset + shadowY} ${x2 + shadowX},${y2 + shadowY}`;
    },
    frontColor(index) {
      if (index === 0) return '#cc0000'; // red
      if (index === 1) return '#cccc00'; // yellow
      if (index === 2) return '#00cc00'; // green
      if (index === 3) return '#cc00cc'; // magenta
      return '#cccccc'; // white
    },
    topColor(index) {
      if (index === 0) return '#ee5555'; // red
      if (index === 1) return '#eeee55'; // yellow
      if (index === 2) return '#55ee55'; // green
      if (index === 3) return '#ee55ee'; // magenta
      return '#eeeeee'; // white
    },
    sideColor(index) {
      if (index === 0) return '#990000'; // red
      if (index === 1) return '#999900'; // yellow
      if (index === 2) return '#009900'; // green
      if (index === 3) return '#990099'; // magenta
      return '#999999'; // white
    }
  },
  computed: {
    svgViewBox() {
      return `0 0 ${this.width} ${this.height}`;
    },
    originPoint() {
      return this.height - (2 * this.padding);
    },
    maxValue() {
      let maxValue = -1;
      for (let i = 0; i < this.stats.length; ++i) {
        if (this.stats[i].max != null && this.stats[i].max > maxValue) {
          maxValue = this.stats[i].max;
        }
      }
      if (maxValue < 0) {
        for (let i = 0; i < this.stats.length; ++i) {
          if (this.stats[i].value != null && this.stats[i].value > maxValue) {
            maxValue = this.stats[i].value;
          }
        }
        return (maxValue < 0) ? 16 : maxValue;
      }
      return maxValue;
    },
    valueSum() {
      let valueSum = 0;
      for (let i = 0; i < this.stats.length; ++i) {
        valueSum += this.stats[i].value;
      }
      return valueSum;
    },
    yIncrement() {
      return (this.maxValue === 0) ? 0 : ((this.originPoint - (2 * this.padding)) / this.maxValue);
    },
    shortNames() {
      const shortNames = [0];
      for (let i = 0; i < this.stats.length; ++i) {
        const stat = this.stats[i];
        if (stat.shortName != null) {
          shortNames.push(stat.shortName);
        } else {
          shortNames.push(stat.name);
        }
      }
      return shortNames;
    }
  }
};
</script>

<style lang="scss" scoped>
.primary-color {
  fill: var(--primaryColor);
}
.barchart-label {
  fill: #cccccc;
  text-transform: uppercase;
}
.barchart-percentage {
  fill: white;
}
@media print {
  @supports (-ms-ime-align:auto) { // a hack for detecting MS Edge... its printing is awful
    .barchart-label {
      fill: black !important;
    }
    .barchart-percentage {
      fill: black !important;
    }
  }
  @supports (-moz-appearance:none) { // a hack for detecting FF... its printing is also awful
    .barchart-label {
      fill: black !important;
    }
    .barchart-percentage {
      fill: black !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/game/ui/BarChart.vue